/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const zhTW: LocalizationResource = {
  locale: 'zh-TW',
  backButton: '返回',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: '默認',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__otherImpersonatorDevice: '其他模擬器設備',
  badge__primary: '主要',
  badge__renewsAt: undefined,
  badge__requiresAction: '需要操作',
  badge__startsAt: undefined,
  badge__thisDevice: '此設備',
  badge__unverified: '未驗證',
  badge__upcomingPlan: undefined,
  badge__userDevice: '用戶設備',
  badge__you: '您',
  commerce: {
    addPaymentMethod: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
    },
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pay: undefined,
    paymentMethods: undefined,
    paymentSource: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    subscribe: undefined,
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToMonthly: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: '創建組織',
    invitePage: {
      formButtonReset: '跳過',
    },
    title: '創建組織',
  },
  dates: {
    lastDay: "昨天{{ date | timeString('zh-TW') }}",
    next6Days: "{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    nextDay: "明天{{ date | timeString('zh-TW') }}",
    numeric: "{{ date | numeric('zh-TW') }}",
    previous6Days: "上週{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    sameDay: "今天{{ date | timeString('zh-TW') }}",
  },
  dividerText: '或者',
  footerActionLink__useAnotherMethod: '使用另一種方法',
  footerPageLink__help: '幫助',
  footerPageLink__privacy: '隱私',
  footerPageLink__terms: '條款',
  formButtonPrimary: '繼續',
  formButtonPrimary__verify: '核實',
  formFieldAction__forgotPassword: '忘記密碼？',
  formFieldError__matchingPasswords: '密碼匹配。',
  formFieldError__notMatchingPasswords: '密碼不匹配。',
  formFieldError__verificationLinkExpired: '驗證連結已過期。請請求新的連結。',
  formFieldHintText__optional: '選填',
  formFieldHintText__slug: 'slug 是人類可讀的 ID，必須是唯一的。它經常在 URL 中使用。',
  formFieldInputPlaceholder__backupCode: undefined,
  formFieldInputPlaceholder__confirmDeletionUserAccount: '刪除帳戶',
  formFieldInputPlaceholder__emailAddress: undefined,
  formFieldInputPlaceholder__emailAddress_username: undefined,
  formFieldInputPlaceholder__emailAddresses: '輸入或黏貼一個或多個電子郵件地址，用空格或逗號分隔',
  formFieldInputPlaceholder__firstName: undefined,
  formFieldInputPlaceholder__lastName: undefined,
  formFieldInputPlaceholder__organizationDomain: undefined,
  formFieldInputPlaceholder__organizationDomainEmailAddress: undefined,
  formFieldInputPlaceholder__organizationName: undefined,
  formFieldInputPlaceholder__organizationSlug: undefined,
  formFieldInputPlaceholder__password: undefined,
  formFieldInputPlaceholder__phoneNumber: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldLabel__automaticInvitations: '為該網域啟用自動邀請',
  formFieldLabel__backupCode: '備用代碼',
  formFieldLabel__confirmDeletion: '確定',
  formFieldLabel__confirmPassword: '確認密碼',
  formFieldLabel__currentPassword: '當前密碼',
  formFieldLabel__emailAddress: '電子郵件地址',
  formFieldLabel__emailAddress_username: '電子郵件地址或使用者名稱',
  formFieldLabel__emailAddresses: '電子郵件地址',
  formFieldLabel__firstName: '名字',
  formFieldLabel__lastName: '姓氏',
  formFieldLabel__newPassword: '新密碼',
  formFieldLabel__organizationDomain: '領域',
  formFieldLabel__organizationDomainDeletePending: '刪除待處理的邀請和建議',
  formFieldLabel__organizationDomainEmailAddress: '驗證電子郵件地址',
  formFieldLabel__organizationDomainEmailAddressDescription: '輸入此網域下的電子郵件地址以接收代碼並驗證此網域名稱。',
  formFieldLabel__organizationName: '組織名稱',
  formFieldLabel__organizationSlug: 'URL 簡稱',
  formFieldLabel__passkeyName: undefined,
  formFieldLabel__password: '密碼',
  formFieldLabel__phoneNumber: '電話號碼',
  formFieldLabel__role: '角色',
  formFieldLabel__signOutOfOtherSessions: '登出所有其他設備',
  formFieldLabel__username: '使用者名稱',
  impersonationFab: {
    action__signOut: '退出登錄',
    title: '以 {{identifier}} 登錄',
  },
  maintenanceMode: undefined,
  membershipRole__admin: '管理員',
  membershipRole__basicMember: '成員',
  membershipRole__guestMember: '訪客',
  organizationList: {
    action__createOrganization: '創建組織',
    action__invitationAccept: '加入',
    action__suggestionsAccept: '申請加入',
    createOrganization: '創建組織',
    invitationAcceptedLabel: '已加入',
    subtitle: '繼續 {{applicationName}}',
    suggestionsAcceptedLabel: '待批准',
    title: '選擇一個帳戶',
    titleWithoutPersonal: '選擇一個組織',
  },
  organizationProfile: {
    badge__automaticInvitation: '自動邀請',
    badge__automaticSuggestion: '自動建議',
    badge__manualInvitation: '不自動註冊',
    badge__unverified: '未經驗證',
    billingPage: {
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle: '新增要驗證的網域。擁有此網域電子郵件地址的使用者可以自動加入該組織或要求加入。',
      title: '新增網域',
    },
    invitePage: {
      detailsTitle__inviteFailed: '邀請無法發送。修覆以下問題然後重試：',
      formButtonPrimary__continue: '發送邀請',
      selectDropdown__role: '選擇角色',
      subtitle: '邀請新成員加入此組織',
      successMessage: '邀請成功發送',
      title: '邀請成員',
    },
    membersPage: {
      action__invite: '邀請',
      action__search: undefined,
      activeMembersTab: {
        menuAction__remove: '移除成員',
        tableHeader__actions: undefined,
        tableHeader__joined: '加入',
        tableHeader__role: '角色',
        tableHeader__user: '用戶',
      },
      detailsTitle__emptyRow: '沒有可顯示的成員',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            '透過將電子郵件網域與您的組織連接來邀請使用者。任何使用匹配電子郵件網域註冊的人都可以隨時加入該組織。',
          headerTitle: '自動邀請',
          primaryButton: '管理已驗證的域名',
        },
        table__emptyRow: '沒有可顯示的邀請',
      },
      invitedMembersTab: {
        menuAction__revoke: '撤銷邀請',
        tableHeader__invited: '已邀請',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: '使用符合的電子郵件網域註冊的用戶將能夠看到請求加入您的組織的建議。',
          headerTitle: '自動建議',
          primaryButton: '管理已驗證的域名',
        },
        menuAction__approve: '批准',
        menuAction__reject: '拒絕',
        tableHeader__requested: '請求存取權限',
        table__emptyRow: '沒有顯示請求',
      },
      start: {
        headerTitle__invitations: '邀請函',
        headerTitle__members: '成員',
        headerTitle__requests: '請求',
      },
    },
    navbar: {
      billing: undefined,
      description: '管理您的組織。',
      general: '一般的',
      members: '成員',
      title: '組織',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: '類型 "{{organizationName}}" 下面繼續。',
          messageLine1: '您確定要刪除該組織嗎？',
          messageLine2: '此操作是永久性的且不可逆轉的。',
          successMessage: '您已刪除該組織。',
          title: '刪除組織',
        },
        leaveOrganization: {
          actionDescription: '類型 "{{organizationName}}" 下面繼續。',
          messageLine1: '您確定要離開此組織嗎？您將失去對此組織及其應用程式的訪問權限。',
          messageLine2: '此操作是永久性的且無法撤銷。',
          successMessage: '您已離開了組織。',
          title: '離開組織',
        },
        title: '危險',
      },
      domainSection: {
        menuAction__manage: '管理',
        menuAction__remove: '刪除',
        menuAction__verify: '核實',
        primaryButton: '新增網域',
        subtitle: '允許使用者自動加入組織或根據已驗證的電子郵件網域請求加入。',
        title: '已驗證域名',
      },
      successMessage: '組織已更新。',
      title: '組織簡介',
    },
    removeDomainPage: {
      messageLine1: '電子郵件域名 {{domain}} 將被刪除。',
      messageLine2: '此後用戶將無法自動加入該組織。',
      successMessage: '{{domain}} 已刪除。',
      title: '刪除網域',
    },
    start: {
      headerTitle__general: '一般的',
      headerTitle__members: '成員',
      profileSection: {
        primaryButton: undefined,
        title: '組織簡介',
        uploadAction__title: '標識',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: '刪除此網域將影響受邀使用者。',
        removeDomainActionLabel__remove: '刪除網域',
        removeDomainSubtitle: '從您的已驗證域名中移除此域名',
        removeDomainTitle: '刪除網域',
      },
      enrollmentTab: {
        automaticInvitationOption__description: '用戶註冊時會自動被邀請加入該組織，並且可以隨時加入。',
        automaticInvitationOption__label: '自動邀請',
        automaticSuggestionOption__description: '使用者會收到加入請求的建議，但必須得到管理員的批准才能加入組織。',
        automaticSuggestionOption__label: '自動建議',
        calloutInfoLabel: '更改註冊模式只會影響新用戶。',
        calloutInvitationCountLabel: '已向用戶發送待處理的邀請: {{count}}',
        calloutSuggestionCountLabel: '已將待處理的建議發送給用戶: {{count}}',
        manualInvitationOption__description: '只能手動邀請使用者加入組織。',
        manualInvitationOption__label: '不自動註冊',
        subtitle: '選擇此網域中的使用者加入組織的方式。',
      },
      start: {
        headerTitle__danger: '危險',
        headerTitle__enrollment: '註冊選項',
      },
      subtitle: '網域 {{domain}} 現已驗證。選擇註冊模式繼續。',
      title: '更新 {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: '輸入發送到您信箱的驗證碼',
      formTitle: '驗證碼',
      resendButton: '沒有收到代碼？重新發送',
      subtitle: '網域 {{domainName}} 需要透過電子郵件進行驗證。',
      subtitleVerificationCodeScreen: '驗證碼已發送至 {{emailAddress}}。輸入代碼以繼續。',
      title: '驗證域名',
    },
  },
  organizationSwitcher: {
    action__createOrganization: '創建組織',
    action__invitationAccept: '加入',
    action__manageOrganization: '管理組織',
    action__suggestionsAccept: '申請加入',
    notSelected: '未選擇組織',
    personalWorkspace: '個人工作區',
    suggestionsAcceptedLabel: '待批准',
  },
  paginationButton__next: '下一頁',
  paginationButton__previous: '上一頁',
  paginationRowText__displaying: '顯示',
  paginationRowText__of: '的',
  reverification: {
    alternativeMethods: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__backupCode: undefined,
      blockButton__emailCode: undefined,
      blockButton__passkey: undefined,
      blockButton__password: undefined,
      blockButton__phoneCode: undefined,
      blockButton__totp: undefined,
      getHelp: {
        blockButton__emailSupport: undefined,
        content: undefined,
        title: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: undefined,
      title: undefined,
    },
    emailCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    noAvailableMethods: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCodeMfa: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    totpMfa: {
      formTitle: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: '新增帳戶',
      action__signOutAll: '登出所有帳戶',
      subtitle: '選擇您想要繼續使用的帳戶。',
      title: '選擇一個帳戶',
    },
    alternativeMethods: {
      actionLink: '獲取幫助',
      actionText: '這些都沒有嗎？',
      blockButton__backupCode: '使用備用代碼',
      blockButton__emailCode: '電子郵件驗證碼到 {{identifier}}',
      blockButton__emailLink: '電子郵件連結到 {{identifier}}',
      blockButton__passkey: undefined,
      blockButton__password: '使用您的密碼登錄',
      blockButton__phoneCode: '發送簡訊代碼到 {{identifier}}',
      blockButton__totp: '使用您的驗證應用程式',
      getHelp: {
        blockButton__emailSupport: '郵件支持',
        content: '如果您在登入帳戶時遇到困難，請給我們發送電子郵件，我們將盡快讓您恢覆訪問。',
        title: '獲取幫助',
      },
      subtitle: '遇到問題了嗎？您可以使用其中任何一種方式登入。',
      title: '使用其他方法',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: '繼續使用 {{applicationName}}',
      title: '輸入備用代碼',
    },
    emailCode: {
      formTitle: '驗證碼',
      resendButton: '重新發送驗證碼',
      subtitle: '繼續使用 {{applicationName}}',
      title: '查看您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      expired: {
        subtitle: '返回原始標籤頁繼續。',
        title: '此驗證連結已過期',
      },
      failed: {
        subtitle: '返回原始標籤頁繼續。',
        title: '此驗證連結無效',
      },
      formSubtitle: '使用發送到您的電子郵件的驗證連結',
      formTitle: '驗證連結',
      loading: {
        subtitle: '即將為您重定向',
        title: '正在登錄...',
      },
      resendButton: '重新發送連結',
      subtitle: '繼續使用 {{applicationName}}',
      title: '查看您的電子郵件',
      unusedTab: {
        title: '您可以關閉此標籤頁',
      },
      verified: {
        subtitle: '即將為您重定向',
        title: '成功登錄',
      },
      verifiedSwitchTab: {
        subtitle: '返回原始標籤頁繼續',
        subtitleNewTab: '返回新打開的標籤頁繼續',
        titleNewTab: '在其他標籤頁上登入',
      },
    },
    forgotPassword: {
      formTitle: '重設密碼代碼',
      resendButton: '重新發送代碼',
      subtitle: '重設您的密碼',
      subtitle_email: '首先，輸入傳送到您的電子郵件 ID 的代碼',
      subtitle_phone: '首先，輸入發送到您手機的代碼',
      title: '重設密碼',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: '重設密碼',
      label__alternativeMethods: '或者，使用其他方式登錄。',
      title: '忘記密碼？',
    },
    noAvailableMethods: {
      message: '無法繼續登錄。沒有可用的身份驗證因素。',
      subtitle: '出現錯誤',
      title: '無法登錄',
    },
    passkey: {
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: '使用其他方法',
      subtitle: '繼續使用 {{applicationName}}',
      title: '輸入您的密碼',
    },
    passwordPwned: {
      title: undefined,
    },
    phoneCode: {
      formTitle: '驗證碼',
      resendButton: '重新發送驗證碼',
      subtitle: '繼續使用 {{applicationName}}',
      title: '檢查手機簡訊',
    },
    phoneCodeMfa: {
      formTitle: '驗證碼',
      resendButton: '重新發送驗證碼',
      subtitle: undefined,
      title: '檢查手機簡訊',
    },
    resetPassword: {
      formButtonPrimary: '重設密碼',
      requiredMessage: '出於安全原因，需要重設您的密碼。',
      successMessage: '您的密碼已成功更改。正在為您登錄，請稍等。',
      title: '重設密碼',
    },
    resetPasswordMfa: {
      detailsLabel: '我們需要驗證您的身份才能重設您的密碼。',
    },
    start: {
      actionLink: '註冊',
      actionLink__join_waitlist: undefined,
      actionLink__use_email: '使用電子郵件',
      actionLink__use_email_username: '使用電子郵件或使用者名稱',
      actionLink__use_passkey: undefined,
      actionLink__use_phone: '使用電話',
      actionLink__use_username: '使用使用者名稱',
      actionText: '還沒有帳戶？',
      actionText__join_waitlist: undefined,
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '繼續使用 {{applicationName}}',
      subtitleCombined: undefined,
      title: '登錄',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: '驗證碼',
      subtitle: undefined,
      title: '兩步驗證',
    },
  },
  signInEnterPasswordTitle: '輸入您的密碼',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: '登錄',
      actionText: '已經有帳戶了？',
      subtitle: '繼續使用 {{applicationName}}',
      title: '填寫缺少的欄位',
    },
    emailCode: {
      formSubtitle: '輸入發送到您的電子郵件地址的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新發送驗證碼',
      subtitle: '繼續使用 {{applicationName}}',
      title: '驗證您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      formSubtitle: '使用發送到您的電子郵件地址的驗證連結',
      formTitle: '驗證連結',
      loading: {
        title: '正在註冊...',
      },
      resendButton: '重新發送連結',
      subtitle: '繼續使用 {{applicationName}}',
      title: '驗證您的電子郵件',
      verified: {
        title: '成功註冊',
      },
      verifiedSwitchTab: {
        subtitle: '返回新打開的標籤頁繼續',
        subtitleNewTab: '返回上一個標籤頁繼續',
        title: '成功驗證電子郵件',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: '我同意 {{ privacyPolicyLink || link("隱私條款") }}',
        label__onlyTermsOfService: '我同意 {{ termsOfServiceLink || link("使用條款") }}',
        label__termsOfServiceAndPrivacyPolicy:
          '我同意 {{ termsOfServiceLink || link("使用條款") }} 和 {{ privacyPolicyLink || link("隱私條款") }}',
      },
      continue: {
        subtitle: '請閱讀並接受條款以繼續',
        title: '同意條款',
      },
    },
    phoneCode: {
      formSubtitle: '輸入發送到您的電話號碼的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新發送驗證碼',
      subtitle: '繼續使用 {{applicationName}}',
      title: '驗證您的電話',
    },
    restrictedAccess: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__emailSupport: undefined,
      blockButton__joinWaitlist: undefined,
      subtitle: undefined,
      subtitleWaitlist: undefined,
      title: undefined,
    },
    start: {
      actionLink: '登錄',
      actionLink__use_email: undefined,
      actionLink__use_phone: undefined,
      actionText: '已經有帳戶了？',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '繼續使用 {{applicationName}}',
      subtitleCombined: '繼續使用 {{applicationName}}',
      title: '創建您的帳戶',
      titleCombined: '創建您的帳戶',
    },
  },
  socialButtonsBlockButton: '使用 {{provider|titleize}} 登錄',
  socialButtonsBlockButtonManyInView: undefined,
  unstable__errors: {
    already_a_member_in_organization: undefined,
    captcha_invalid: '由於安全驗證失敗，註冊未成功。請刷新頁面重試或聯絡支持獲取更多幫助。',
    captcha_unavailable: '由於機器人驗證失敗，註冊失敗。請重新整理頁面重試或聯絡支援人員以取得更多協助。',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: '我們無法找到具有這些信息的帳戶。',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: '電子郵件地址必須是有效的電子郵件地址。',
    form_param_format_invalid__phone_number: '電話號碼必須採用有效的國際格式。',
    form_param_max_length_exceeded__first_name: '名字不超過 256 個字元。',
    form_param_max_length_exceeded__last_name: '姓氏不得超過 256 個字元。',
    form_param_max_length_exceeded__name: '名稱不應超過 256 個字元。',
    form_param_nil: undefined,
    form_param_value_invalid: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: undefined,
    form_password_not_strong_enough: '您的密碼強度不夠。',
    form_password_pwned: '這個密碼在數據洩露中被發現，不能使用，請換一個密碼試試。',
    form_password_pwned__sign_in: undefined,
    form_password_size_in_bytes_exceeded: '您的密碼超過了允許的最大位元組數，請縮短它或去掉一些特殊字元。',
    form_password_validation_failed: '密碼錯誤',
    form_username_invalid_character: undefined,
    form_username_invalid_length: undefined,
    identification_deletion_failed: '您無法刪除您的最後一個身分證明。',
    not_allowed_access:
      "您使用的電子郵件地址或電話號碼不允許註冊。這可能因為您在電子郵件地址中使用了 '+', '=', '#' 或 '.'，使用了與臨時電子郵件服務關聯的域名，或者有明確的排除。如果您認為這是錯誤，請聯絡支持。",
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    passkey_already_exists: undefined,
    passkey_not_supported: undefined,
    passkey_pa_not_supported: undefined,
    passkey_registration_cancelled: undefined,
    passkey_retrieval_cancelled: undefined,
    passwordComplexity: {
      maximumLength: '少於{{length}}個字元',
      minimumLength: '{{length}}個或更多字元',
      requireLowercase: '一個小寫字母',
      requireNumbers: '一個數字',
      requireSpecialCharacter: '一個特殊字元',
      requireUppercase: '一個大寫字母',
      sentencePrefix: '您的密碼必須包含',
    },
    phone_number_exists: '這個電話號碼已被使用。請嘗試另一個。',
    session_exists: '您已經登錄。',
    web3_missing_identifier: undefined,
    zxcvbn: {
      couldBeStronger: '您的密碼可以用，但可以更強。試著添加更多字元。',
      goodPassword: '做得好。這是一個優秀的密碼。',
      notEnough: '您的密碼強度不夠。',
      suggestions: {
        allUppercase: '大寫一些，但不是所有的字母。',
        anotherWord: '添加更不常見的更多單字。',
        associatedYears: '避免與你有關的年份。',
        capitalization: '大寫不僅僅是第一個字母。',
        dates: '避免與你有關的日期和年份。',
        l33t: '避免預測的字母替換，如"@"代替"a"。',
        longerKeyboardPattern: '使用更長的鍵盤模式，並多次改變打字方向。',
        noNeed: '你可以創建強密碼，而無需使用符號，數字或大寫字母。',
        pwned: '如果您在其他地方使用此密碼，您應該更改它。',
        recentYears: '避免近年來。',
        repeated: '避免重複的單字和字元。',
        reverseWords: '避免常用詞的反向拼寫。',
        sequences: '避免常見字元序列。',
        useWords: '使用多個單字，但避免常見短語。',
      },
      warnings: {
        common: '這是一個常用的密碼。',
        commonNames: '常見的名字和姓氏易被猜到。',
        dates: '日期易被猜到。',
        extendedRepeat: '像"abcabcabc"這樣的重複字元模式易被猜到。',
        keyPattern: '短鍵盤模式易被猜到。',
        namesByThemselves: '單個名字或姓氏易被猜到。',
        pwned: '您的密碼在網路上的數據洩露中被暴露。',
        recentYears: '近年來易被猜到。',
        sequences: '像"abc"這樣的常見字元序列易被猜到。',
        similarToCommon: '這個密碼和常用密碼相似。',
        simpleRepeat: '像"aaa"這樣的重複字元易被猜到。',
        straightRow: '鍵盤上的直行鍵易被猜到。',
        topHundred: '這是一個頻繁使用的密碼。',
        topTen: '這是一個大量使用的密碼。',
        userInputs: '不應該有任何個人或頁面相關的數據。',
        wordByItself: '單個單字易被猜到。',
      },
    },
  },
  userButton: {
    action__addAccount: '添加帳戶',
    action__manageAccount: '管理帳戶',
    action__signOut: '退出登錄',
    action__signOutAll: '退出所有帳戶',
  },
  userProfile: {
    backupCodePage: {
      actionLabel__copied: '已複製！',
      actionLabel__copy: '複製全部',
      actionLabel__download: '下載 .txt',
      actionLabel__print: '列印',
      infoText1: '將為此帳戶啟用備份代碼。',
      infoText2: '保密並安全儲存備份代碼。如果您懷疑它們已經洩露，您可以重新生成備份代碼。',
      subtitle__codelist: '安全儲存並保守秘密。',
      successMessage:
        '現在已啟用備份代碼。如果您失去了驗證設備的訪問權限，您可以使用其中之一登入您的帳戶。每個代碼只能使用一次。',
      successSubtitle: '如果您失去了驗證設備的訪問權限，您可以使用其中之一登入您的帳戶。',
      title: '添加備份代碼驗證',
      title__codelist: '備份代碼',
    },
    billingPage: {
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: '選擇一個供應商來連接您的帳戶。',
      formHint__noAccounts: '沒有可用的外部帳戶供應商。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用這個已連接的帳戶，任何依賴的功能將不再工作。',
        successMessage: '{{connectedAccount}} 已從您的帳戶中移除。',
        title: '移除已連接的帳戶',
      },
      socialButtonsBlockButton: '連接 {{provider|titleize}} 帳戶',
      successMessage: '供應商已被添加到您的帳戶',
      title: '添加已連接的帳戶',
    },
    deletePage: {
      actionDescription: '請在下方輸入“Delete account”以繼續。',
      confirm: '移除帳戶',
      messageLine1: '您確定要刪除您的帳戶嗎？',
      messageLine2: '這個動作是永久性且無法還原的。',
      title: '移除帳戶',
    },
    emailAddressPage: {
      emailCode: {
        formHint: '一封含有驗證碼的郵件將會被發送到這個電子郵件地址。',
        formSubtitle: '輸入發送到 {{identifier}} 的驗證碼',
        formTitle: '驗證碼',
        resendButton: '重發驗證碼',
        successMessage: '電子郵件 {{identifier}} 已被添加到您的帳戶。',
      },
      emailLink: {
        formHint: '一封含有驗證連結的郵件將會被發送到這個電子郵件地址。',
        formSubtitle: '點擊發送到 {{identifier}} 的郵件中的驗證連結',
        formTitle: '驗證連結',
        resendButton: '重發連結',
        successMessage: '電子郵件 {{identifier}} 已被添加到您的帳戶。',
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法使用這個電子郵件地址登錄。',
        successMessage: '電子郵件 {{emailAddress}} 已從您的帳戶中移除。',
        title: '移除電子郵件地址',
      },
      title: '添加電子郵件地址',
      verifyTitle: 'Verify email address',
    },
    formButtonPrimary__add: 'Add',
    formButtonPrimary__continue: '繼續',
    formButtonPrimary__finish: '完成',
    formButtonPrimary__remove: 'Remove',
    formButtonPrimary__save: 'Save',
    formButtonReset: '取消',
    mfaPage: {
      formHint: '選擇一個添加的方法。',
      title: '添加兩步驗證',
    },
    mfaPhoneCodePage: {
      backButton: '使用現有號碼',
      primaryButton__addPhoneNumber: '添加電話號碼',
      removeResource: {
        messageLine1: '{{identifier}} 將不再在登錄時接收驗證代碼。',
        messageLine2: '您的帳戶可能不再安全。您確定要繼續嗎？',
        successMessage: '已移除{{mfaPhoneCode}}的簡訊驗證碼兩步驗證',
        title: '移除兩步驗證',
      },
      subtitle__availablePhoneNumbers: '選擇一個電話號碼來註冊簡訊驗證碼兩步驗證。',
      subtitle__unavailablePhoneNumbers: '沒有可用的電話號碼來註冊簡訊驗證碼兩步驗證。',
      successMessage1: '登入時，您需要輸入發送到此電話號碼的驗證碼作為附加步驟。',
      successMessage2: '保存這些備份代碼並將其儲存在安全的地方。如果您無法存取身份驗證設備，則可以使用備用代碼登入。',
      successTitle: '已啟用簡訊驗證碼',
      title: '添加簡訊驗證碼驗證',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: '掃描二維碼',
        buttonUnableToScan__nonPrimary: '不能掃描二維碼？',
        infoText__ableToScan: '在您的驗證器應用中設置一個新的登錄方法，並掃描下面的二維碼將其連結到您的帳戶。',
        infoText__unableToScan: '在驗證器中設置一個新的登錄方法，並輸入下面提供的 Key。',
        inputLabel__unableToScan1: '確保啟用了基於時間或一次性密碼，然後完成連結您的帳戶。',
        inputLabel__unableToScan2: '或者，如果您的驗證器支持 TOTP URIs，您也可以複製完整的 URI。',
      },
      removeResource: {
        messageLine1: '登錄時，將不再需要來自此驗證器的驗證碼。',
        messageLine2: '您的帳戶可能不再安全。您確定要繼續嗎？',
        successMessage: '已移除通過驗證器應用程式的兩步驗證。',
        title: '移除兩步驗證',
      },
      successMessage: '現在已啟用兩步驗證。在登錄時，您需要輸入來自此驗證器的驗證碼作為額外步驟。',
      title: '添加驗證器應用程式',
      verifySubtitle: '輸入您的驗證器生成的驗證碼',
      verifyTitle: '驗證代碼',
    },
    mobileButton__menu: '菜單',
    navbar: {
      account: '輪廓',
      billing: undefined,
      description: '管理您的帳戶資訊。',
      security: '安全',
      title: '帳戶',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: undefined,
        title: undefined,
      },
      subtitle__rename: undefined,
      title__rename: undefined,
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions: '建議退出所有可能使用過舊密碼的其他裝置。',
      readonly: '您的密碼目前無法編輯，因為您只能透過企業連線登入。',
      successMessage__set: '您的密碼已設置。',
      successMessage__signOutOfOtherSessions: '所有其他設備已退出。',
      successMessage__update: '您的密碼已更新。',
      title__set: '設置密碼',
      title__update: '更改密碼',
    },
    phoneNumberPage: {
      infoText: '一條包含驗證連結的簡訊將會發送到這個電話號碼。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法使用這個電話號碼登錄。',
        successMessage: '電話號碼 {{phoneNumber}} 已從您的帳戶中移除。',
        title: '移除電話號碼',
      },
      successMessage: '{{identifier}} 已被添加到您的帳戶。',
      title: '添加電話號碼',
      verifySubtitle: '輸入發送至的驗證碼 {{identifier}}',
      verifyTitle: '驗證電話號碼',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: '上傳小於10MB的JPG, PNG, GIF, 或WEBP格式的圖片',
      imageFormDestructiveActionSubtitle: '移除圖片',
      imageFormSubtitle: '上傳圖片',
      imageFormTitle: '個人資料圖片',
      readonly: '您的個人資料資訊已由企業連線提供，無法編輯。',
      successMessage: '您的個人資料已更新。',
      title: '更新個人資料',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: '退出設備',
        title: '活動設備',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: '再試一次',
        actionLabel__reauthorize: '立即授權',
        destructiveActionTitle: '移除',
        primaryButton: '連接帳戶',
        subtitle__disconnected: undefined,
        subtitle__reauthorize: '所需範圍已更新，您可能會遇到功能受限的情況。請重新授權此應用程式以避免任何問題。',
        title: '已連接的帳戶',
      },
      dangerSection: {
        deleteAccountButton: '移除帳戶',
        title: '危險',
      },
      emailAddressesSection: {
        destructiveAction: '移除電子郵件地址',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '添加電子郵件地址',
        title: '電子郵件地址',
      },
      enterpriseAccountsSection: {
        title: '企業帳戶',
      },
      headerTitle__account: '帳戶',
      headerTitle__security: '安全',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: '重新生成代碼',
          headerTitle: '備份代碼',
          subtitle__regenerate: '獲取一套新的安全備份代碼。之前的備份代碼將被刪除，無法使用。',
          title__regenerate: '重新生成備份代碼',
        },
        phoneCode: {
          actionLabel__setDefault: '設為默認',
          destructiveActionLabel: '移除電話號碼',
        },
        primaryButton: '添加兩步驗證',
        title: '兩步驗證',
        totp: {
          destructiveActionTitle: '移除',
          headerTitle: '驗證器應用程式',
        },
      },
      passkeysSection: {
        menuAction__destructive: undefined,
        menuAction__rename: undefined,
        primaryButton: undefined,
        title: undefined,
      },
      passwordSection: {
        primaryButton__setPassword: '設置密碼',
        primaryButton__updatePassword: '更改密碼',
        title: '密碼',
      },
      phoneNumbersSection: {
        destructiveAction: '移除電話號碼',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '添加電話號碼',
        title: '電話號碼',
      },
      profileSection: {
        primaryButton: '更新個人資料',
        title: '個人資料',
      },
      usernameSection: {
        primaryButton__setUsername: '設置使用者名稱',
        primaryButton__updateUsername: '更改使用者名稱',
        title: '使用者名稱',
      },
      web3WalletsSection: {
        destructiveAction: '移除錢包',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Web3 錢包',
        title: 'Web3 錢包',
      },
    },
    usernamePage: {
      successMessage: '您的使用者名稱已更新。',
      title__set: '更新使用者名稱',
      title__update: '更新使用者名稱',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法使用這個 web3 錢包登錄。',
        successMessage: '{{web3Wallet}} 已從您的帳戶中移除。',
        title: '移除 web3 錢包',
      },
      subtitle__availableWallets: '選擇一個 web3 錢包連接到您的帳戶。',
      subtitle__unavailableWallets: '沒有可用的 web3 錢包。',
      successMessage: '錢包已被添加到您的帳戶。',
      title: '添加web3錢包',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: undefined,
      actionText: undefined,
      formButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    success: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
} as const;
